/**
 * Notification Center Data Models
 *
 * This file defines all types and interfaces for the unified notification system
 * that replaces the old Alert Center. The system is order-centric, supporting
 * all 9 workflow phases plus parallel processes (KYC and Credit Verification).
 */

/**
 * Notification Categories - High-level grouping for filtering and display
 */
export type NotificationCategory =
  | 'order' // Order workflow events (status changes, phase transitions, blocks)
  | 'approval' // Items requiring approval (proforma, margin, PO, credit override)
  | 'document' // Document-related events (missing, expiring, verification issues)
  | 'customer' // Customer events (new customer, KYC, credit)
  | 'supplier' // Supplier events (quotes, QTEs, PO confirmations)
  | 'payment' // Payment events (received, overdue, credit line)
  | 'shipping' // Shipping/delivery events (booking, departure, delivery)
  | 'system'; // System/AI events (processing complete, errors)

/**
 * Notification Types - Specific event types for detailed categorization
 */
export type NotificationType =
  // Order Events
  | 'order_received'
  | 'order_status_changed'
  | 'order_phase_transition'
  | 'order_blocked'
  | 'order_unblocked'
  | 'order_delayed'
  | 'order_split'

  // Approval Events (Action Required)
  | 'approval_proforma'
  | 'approval_margin'
  | 'approval_po'
  | 'approval_credit_override'
  | 'approval_document_amendment'
  | 'approval_price_change'

  // Document Events
  | 'document_missing'
  | 'document_expiring'
  | 'document_verification_issue'
  | 'document_verified'
  | 'document_amendment_requested'
  | 'document_deadline'
  | 'document_received'
  | 'document_rejected'

  // Customer Events
  | 'customer_new'
  | 'customer_kyc_pending'
  | 'customer_kyc_completed'
  | 'customer_credit_approved'
  | 'customer_credit_denied'
  | 'customer_credit_line_warning'
  | 'customer_at_risk'

  // Supplier Events
  | 'supplier_quote_received'
  | 'supplier_qte_sent'
  | 'supplier_qte_opened'
  | 'supplier_qte_responded'
  | 'supplier_qte_expired'
  | 'supplier_po_sent'
  | 'supplier_po_confirmed'
  | 'supplier_po_issue'
  | 'supplier_production_started'
  | 'supplier_production_complete'

  // Payment Events
  | 'payment_received'
  | 'payment_partial'
  | 'payment_overdue'
  | 'payment_reminder_sent'
  | 'credit_line_exceeded_warning'
  | 'payment_reconciled'

  // Shipping Events
  | 'booking_confirmed'
  | 'vessel_scheduled'
  | 'vessel_departed'
  | 'transshipment_arrived'
  | 'documents_sent_dhl'
  | 'documents_delivered'
  | 'customs_clearance'
  | 'delivery_confirmed'
  | 'delivery_delayed'

  // System Events
  | 'ai_extraction_complete'
  | 'ai_processing_started'
  | 'ai_processing_error'
  | 'system_warning'
  | 'system_error'
  | 'webhook_received';

/**
 * Priority levels for notifications - determines visual treatment and sorting
 */
export type NotificationPriority = 'critical' | 'high' | 'medium' | 'low';

/**
 * Notification state for read/archive tracking
 */
export type NotificationState = 'unread' | 'read' | 'archived' | 'dismissed';

/**
 * Action types that can be taken directly from a notification
 */
export type NotificationActionType =
  | 'navigate' // Navigate to a page/route
  | 'approve' // Approve something (proforma, margin, etc.)
  | 'reject' // Reject something
  | 'snooze' // Snooze the notification
  | 'dismiss' // Dismiss the notification
  | 'resolve' // Mark as resolved
  | 'upload' // Upload a document
  | 'verify' // Verify a document
  | 'contact' // Contact customer/supplier
  | 'view' // View details
  | 'send_reminder' // Send reminder email to customer/supplier
  | 'compare_quotes' // Navigate to quote comparison page
  | 'request_documents' // Request missing documents from customer
  | 'track_shipment' // Open shipment tracking
  | 'custom'; // Custom action

/**
 * Action that can be taken on a notification
 */
export interface NotificationAction {
  id: string;
  type: NotificationActionType;
  label: string;
  icon?: string; // Font Awesome icon class
  route?: string; // For navigate actions
  queryParams?: Record<string, string>;
  data?: Record<string, unknown>; // Additional action data
  isPrimary?: boolean; // Primary action gets prominent styling
  isDanger?: boolean; // Danger action (delete, reject)
}

/**
 * Related entity reference for linking to other parts of the system
 */
export interface NotificationEntityRef {
  type: 'order' | 'customer' | 'supplier' | 'document' | 'qte' | 'quote' | 'payment' | 'shipment';
  id: string;
  displayName?: string;
  route?: string;
}

/**
 * Thread for grouping related notifications (typically by order)
 */
export interface NotificationThread {
  id: string;
  orderId: string;
  orderNumber: string;
  customerName?: string;
  count: number;
  unreadCount: number;
  urgentCount: number;
  latestAt: string;
  notifications: Notification[];
  isExpanded?: boolean;
}

/**
 * Main Notification interface
 */
export interface Notification {
  // Core identifiers
  id: string;
  threadId?: string; // For grouping related notifications

  // Classification
  category: NotificationCategory;
  type: NotificationType;
  priority: NotificationPriority;

  // Content
  title: string;
  message: string;
  icon?: string; // Font Awesome icon class

  // Order context (primary relationship - order-centric design)
  orderId?: string;
  orderNumber?: string; // e.g., "V25-530"
  workflowPhase?: number; // 1-9
  statusId?: string; // OrderStatusId reference

  // Related entities
  customerId?: string;
  customerName?: string;
  supplierId?: string;
  supplierName?: string;
  documentType?: string;
  documentId?: string;
  entities?: NotificationEntityRef[];

  // State
  state: NotificationState;

  // Timestamps
  createdAt: string;
  readAt?: string;
  archivedAt?: string;

  // Snooze
  snoozedUntil?: string;

  // Resolution
  isResolved?: boolean;
  resolvedAt?: string;
  resolvedBy?: string;
  resolvedNotes?: string;

  // Due date (for deadline-based notifications)
  dueDate?: string;

  // Actions available on this notification
  actions?: NotificationAction[];

  // Metadata for custom handling
  metadata?: Record<string, unknown>;

  // Source tracking
  source: 'system' | 'user' | 'webhook' | 'ai';
  sourceUserId?: string;
  sourceUserName?: string;
}

/**
 * Notification preferences per user
 */
export interface NotificationPreferences {
  userId: string;

  // Category-level preferences
  categories: Record<
    NotificationCategory,
    {
      enabled: boolean;
      emailEnabled: boolean;
      pushEnabled: boolean;
      soundEnabled: boolean;
    }
  >;

  // Type-level overrides (optional)
  typeOverrides?: Partial<
    Record<
      NotificationType,
      {
        enabled?: boolean;
        emailEnabled?: boolean;
        pushEnabled?: boolean;
      }
    >
  >;

  // General settings
  soundEnabled: boolean;
  quietHoursStart?: string; // HH:mm format
  quietHoursEnd?: string;
  groupByOrder: boolean;
  autoArchiveDays: number; // Auto-archive after N days
}

/**
 * Notification counts for badge display and summary
 */
export interface NotificationCounts {
  total: number;
  unread: number;
  critical: number;
  high: number;
  requiresAction: number;
  byCategory: Record<NotificationCategory, number>;
}

/**
 * Filter options for notification queries
 */
export interface NotificationFilters {
  categories?: NotificationCategory[];
  types?: NotificationType[];
  priorities?: NotificationPriority[];
  states?: NotificationState[];
  orderId?: string;
  orderNumber?: string;
  customerId?: string;
  supplierId?: string;
  workflowPhase?: number;
  fromDate?: string;
  toDate?: string;
  hasActions?: boolean;
  isResolved?: boolean;
  searchQuery?: string;
  // Pagination
  page?: number;
  pageSize?: number;
}

/**
 * Paginated API response structure
 */
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/**
 * Pagination state for UI
 */
export interface PaginationState {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/**
 * A message on the notifications socket, as the server sends it (apps/alerts/consumers.py): 'alert' (an alert saved
 * to the bell, card 927) and 'notification' (a live notice), each under its own key; the bell rereads for both.
 */
export type NotificationWsMessage =
  | { type: 'alert'; alert: Record<string, unknown> }
  | { type: 'notification'; notification: Record<string, unknown> };

/**
 * Quick filter presets for the UI
 */
export type NotificationQuickFilter = 'all' | 'unread' | 'action_required' | 'by_order';

/**
 * Category configuration for UI display
 */
export interface NotificationCategoryConfig {
  category: NotificationCategory;
  label: string; // translation key
  icon: string;
  color: string; // Hex color
  bgGradientStart: string; // rgba color
  bgGradientEnd: string; // rgba color
}

/**
 * Default category configurations
 */
export const NOTIFICATION_CATEGORY_CONFIG: Record<
  NotificationCategory,
  NotificationCategoryConfig
> = {
  order: {
    category: 'order',
    label: 'notificationCenter.category.order',
    icon: 'fa-shopping-cart',
    color: '#FF6600',
    bgGradientStart: 'rgba(249, 115, 22, 0.08)',
    bgGradientEnd: 'rgba(249, 115, 22, 0.03)',
  },
  approval: {
    category: 'approval',
    label: 'notificationCenter.category.approval',
    icon: 'fa-check-circle',
    color: '#ef4444',
    bgGradientStart: 'rgba(239, 68, 68, 0.08)',
    bgGradientEnd: 'rgba(239, 68, 68, 0.03)',
  },
  document: {
    category: 'document',
    label: 'notificationCenter.category.document',
    icon: 'fa-file-alt',
    color: '#10b981',
    bgGradientStart: 'rgba(16, 185, 129, 0.08)',
    bgGradientEnd: 'rgba(16, 185, 129, 0.03)',
  },
  customer: {
    category: 'customer',
    label: 'notificationCenter.category.customer',
    icon: 'fa-building',
    color: '#FF6600',
    bgGradientStart: 'rgba(139, 92, 246, 0.08)',
    bgGradientEnd: 'rgba(139, 92, 246, 0.03)',
  },
  supplier: {
    category: 'supplier',
    label: 'notificationCenter.category.supplier',
    icon: 'fa-industry',
    color: '#FF6600',
    bgGradientStart: 'rgba(59, 130, 246, 0.08)',
    bgGradientEnd: 'rgba(59, 130, 246, 0.03)',
  },
  payment: {
    category: 'payment',
    label: 'notificationCenter.category.payment',
    icon: 'fa-dollar-sign',
    color: '#f59e0b',
    bgGradientStart: 'rgba(245, 158, 11, 0.08)',
    bgGradientEnd: 'rgba(245, 158, 11, 0.03)',
  },
  shipping: {
    category: 'shipping',
    label: 'notificationCenter.category.shipping',
    icon: 'fa-ship',
    color: '#FF6600',
    bgGradientStart: 'rgba(6, 182, 212, 0.08)',
    bgGradientEnd: 'rgba(6, 182, 212, 0.03)',
  },
  system: {
    category: 'system',
    label: 'notificationCenter.category.system',
    icon: 'fa-cog',
    color: '#6b7280',
    bgGradientStart: 'rgba(107, 114, 128, 0.08)',
    bgGradientEnd: 'rgba(107, 114, 128, 0.03)',
  },
};

/**
 * Priority configuration for UI display
 */
export const NOTIFICATION_PRIORITY_CONFIG: Record<
  NotificationPriority,
  { label: string; color: string; bgColor: string }
> = {
  critical: {
    label: 'notificationCenter.priority.critical',
    color: '#dc2626',
    bgColor: 'rgba(220, 38, 38, 0.1)',
  },
  high: {
    label: 'notificationCenter.priority.high',
    color: '#ea580c',
    bgColor: 'rgba(234, 88, 12, 0.1)',
  },
  medium: {
    label: 'notificationCenter.priority.medium',
    color: '#d97706',
    bgColor: 'rgba(217, 119, 6, 0.1)',
  },
  low: {
    label: 'notificationCenter.priority.low',
    color: '#6b7280',
    bgColor: 'rgba(107, 114, 128, 0.1)',
  },
};

/**
 * Default notification counts
 */
export const DEFAULT_NOTIFICATION_COUNTS: NotificationCounts = {
  total: 0,
  unread: 0,
  critical: 0,
  high: 0,
  requiresAction: 0,
  byCategory: {
    order: 0,
    approval: 0,
    document: 0,
    customer: 0,
    supplier: 0,
    payment: 0,
    shipping: 0,
    system: 0,
  },
};
